/**
 * GDPR Section component for patron account page.
 *
 * Provides patrons with access to their GDPR rights:
 * - View what data is collected and why
 * - Export personal data (right of access / portability)
 * - Request account deletion (right to erasure)
 */

import { useState, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { portalFetch } from "../contexts/PatronAuthContext";

interface GdprInfo {
  data_categories: Array<{
    category: string;
    examples: string[];
    purpose: string;
    legal_basis: string;
  }>;
  retention_policy: {
    loan_history_days: number;
    inactive_account_days: number;
  };
  rights: Record<string, string>;
}

interface DeletionCheck {
  can_delete: boolean;
  blockers: Array<{
    type: string;
    message: string;
    count?: number;
    amount_cents?: number;
  }>;
  method: "anonymization" | "deletion";
  warning: string | null;
}

export function GdprSection() {
  const [showDetails, setShowDetails] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);

  // Fetch GDPR info
  const { data: gdprInfo, isLoading: infoLoading } = useQuery<GdprInfo>({
    queryKey: ["portal", "gdpr", "info"],
    queryFn: async (): Promise<GdprInfo> => {
      const response = await portalFetch("/gdpr/info");
      if (!response.ok) throw new Error("Failed to fetch GDPR info");
      return (await response.json()) as GdprInfo;
    },
    enabled: showDetails,
  });

  // Check deletion eligibility
  const { data: deletionCheck, refetch: checkDeletion } = useQuery<DeletionCheck>({
    queryKey: ["portal", "gdpr", "deletion-check"],
    queryFn: async (): Promise<DeletionCheck> => {
      const response = await portalFetch("/gdpr/deletion-check");
      if (!response.ok) throw new Error("Failed to check deletion eligibility");
      return (await response.json()) as DeletionCheck;
    },
    enabled: showDeleteConfirm,
  });

  // Export mutation
  const exportMutation = useMutation({
    mutationFn: async (format: "json" | "csv"): Promise<Blob> => {
      const response = await portalFetch(`/gdpr/export?format=${format}`);
      if (!response.ok) throw new Error("Export failed");
      return response.blob();
    },
    onSuccess: (blob, format) => {
      // Trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `donnees-personnelles-${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
  });

  // Deletion mutation
  const deleteMutation = useMutation({
    mutationFn: async (): Promise<{ success: boolean; message: string }> => {
      const response = await portalFetch("/gdpr/deletion-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reason: deleteReason || undefined,
          confirmed: true,
        }),
      });
      if (!response.ok) {
        const errorData = (await response.json()) as { error?: string };
        throw new Error(errorData.error ?? "Deletion request failed");
      }
      return (await response.json()) as { success: boolean; message: string };
    },
    onSuccess: () => {
      // Redirect to login after successful deletion
      window.location.href = "/opac/login?deleted=1";
    },
  });

  const handleExport = useCallback(
    (format: "json" | "csv") => {
      exportMutation.mutate(format);
    },
    [exportMutation]
  );

  const handleDeleteRequest = useCallback(() => {
    if (deleteConfirmed) {
      deleteMutation.mutate();
    }
  }, [deleteConfirmed, deleteMutation]);

  const toggleDetails = useCallback(() => {
    setShowDetails((prev) => !prev);
  }, []);

  const toggleDeleteConfirm = useCallback(() => {
    setShowDeleteConfirm((prev) => !prev);
    if (!showDeleteConfirm) {
      void checkDeletion();
    }
    setDeleteConfirmed(false);
    setDeleteReason("");
  }, [showDeleteConfirm, checkDeletion]);

  return (
    <section
      aria-labelledby="gdpr-heading"
      className="rounded-xl bg-white p-6 shadow-sm dark:bg-gray-800"
    >
      <h2
        id="gdpr-heading"
        className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white"
      >
        <svg
          className="h-5 w-5 text-blue-600 dark:text-blue-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
          />
        </svg>
        Mes données personnelles
      </h2>

      <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
        Conformément au RGPD, vous pouvez consulter, exporter ou demander la suppression de vos
        données personnelles.
      </p>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={toggleDetails}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          aria-expanded={showDetails}
        >
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {showDetails ? "Masquer les détails" : "Quelles données collectons-nous ?"}
        </button>

        <button
          type="button"
          onClick={() => {
            handleExport("json");
          }}
          disabled={exportMutation.isPending}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none disabled:opacity-50"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
          {exportMutation.isPending ? "Export en cours..." : "Exporter mes données"}
        </button>

        <button
          type="button"
          onClick={() => {
            handleExport("csv");
          }}
          disabled={exportMutation.isPending}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
        >
          Exporter en CSV
        </button>
      </div>

      {/* Error message */}
      {exportMutation.isError && (
        <p className="mt-3 text-sm text-red-600 dark:text-red-400" role="alert">
          Erreur lors de l'export. Veuillez réessayer.
        </p>
      )}

      {/* Data details panel */}
      {showDetails && (
        <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
          {infoLoading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Chargement...</p>
          ) : gdprInfo ? (
            <div className="space-y-4">
              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">
                  Données collectées
                </h3>
                <ul className="space-y-2 text-sm">
                  {gdprInfo.data_categories.map((cat) => (
                    <li key={cat.category} className="text-gray-600 dark:text-gray-400">
                      <strong>{cat.category}</strong> : {cat.examples.join(", ")}
                      <br />
                      <span className="text-xs text-gray-500 dark:text-gray-500">
                        Finalité : {cat.purpose} • Base légale : {cat.legal_basis}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">
                  Durées de conservation
                </h3>
                <ul className="text-sm text-gray-600 dark:text-gray-400">
                  <li>
                    Historique des prêts : {gdprInfo.retention_policy.loan_history_days} jours après
                    retour
                  </li>
                  <li>
                    Compte inactif : {gdprInfo.retention_policy.inactive_account_days} jours
                    d'inactivité
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">
                  Vos droits
                </h3>
                <ul className="text-sm text-gray-600 dark:text-gray-400">
                  {Object.entries(gdprInfo.rights).map(([key, value]) => (
                    <li key={key}>
                      <strong className="capitalize">{key.replace(/_/g, " ")}</strong> : {value}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Delete account section */}
      <div className="mt-6 border-t border-gray-200 pt-6 dark:border-gray-700">
        <button
          type="button"
          onClick={toggleDeleteConfirm}
          className="text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
          aria-expanded={showDeleteConfirm}
        >
          {showDeleteConfirm ? "Annuler" : "Supprimer mon compte"}
        </button>

        {showDeleteConfirm && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-900/20">
            <h3 className="mb-2 font-semibold text-red-800 dark:text-red-300">
              Supprimer votre compte
            </h3>

            {deletionCheck && !deletionCheck.can_delete ? (
              <div>
                <p className="mb-3 text-sm text-red-700 dark:text-red-400">
                  Vous ne pouvez pas supprimer votre compte pour le moment :
                </p>
                <ul className="mb-3 list-inside list-disc text-sm text-red-700 dark:text-red-400">
                  {deletionCheck.blockers.map((blocker) => (
                    <li key={blocker.type}>{blocker.message}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <div>
                {deletionCheck?.warning && (
                  <p className="mb-3 text-sm text-red-700 dark:text-red-400">
                    ⚠️ {deletionCheck.warning}
                  </p>
                )}

                <div className="mb-3">
                  <label
                    htmlFor="delete-reason"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Raison (facultatif)
                  </label>
                  <input
                    type="text"
                    id="delete-reason"
                    value={deleteReason}
                    onChange={(e) => {
                      setDeleteReason(e.target.value);
                    }}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                    placeholder="Pourquoi souhaitez-vous supprimer votre compte ?"
                  />
                </div>

                <div className="mb-4">
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={deleteConfirmed}
                      onChange={(e) => {
                        setDeleteConfirmed(e.target.checked);
                      }}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <span className="text-gray-700 dark:text-gray-300">
                      Je comprends que cette action est irréversible et que toutes mes données
                      personnelles seront supprimées.
                    </span>
                  </label>
                </div>

                <button
                  type="button"
                  onClick={handleDeleteRequest}
                  disabled={!deleteConfirmed || deleteMutation.isPending}
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {deleteMutation.isPending
                    ? "Suppression en cours..."
                    : "Confirmer la suppression"}
                </button>

                {deleteMutation.isError && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">
                    {deleteMutation.error instanceof Error
                      ? deleteMutation.error.message
                      : "Erreur lors de la suppression"}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export default GdprSection;
